export type CoordinateValidationError = { [key: string]: boolean };

export type CoordinateValidationConfig = {
  min: number;
  max: number;
  regex: RegExp;
  errorKey: string;
};

export const LATITUDE_REGEX = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/;
export const LONGITUDE_REGEX = /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

/**
 * Shared coordinate validator used by both:
 * - Angular form validators (returns an error map / null)
 * - Map rendering guards (via boolean wrappers)
 *
 * Accepts both numbers and numeric strings.
 */
export function validateCoordinate(
  value: unknown,
  { min, max, regex, errorKey }: CoordinateValidationConfig,
): CoordinateValidationError | null {
  // Reject null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return { [errorKey]: true };
  }

  let stringValue: string;
  let numValue: number;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { [errorKey]: true };
    }
    stringValue = String(value);
    numValue = value;
  } else {
    stringValue = String(value).trim();

    if (stringValue === '') {
      return { [errorKey]: true };
    }

    numValue = Number.parseFloat(stringValue);

    if (!Number.isFinite(numValue)) {
      return { [errorKey]: true };
    }
  }

  if (numValue < min || numValue > max) {
    return { [errorKey]: true };
  }

  if (!regex.test(stringValue)) {
    return { [errorKey]: true };
  }

  return null;
}

export function isValidLatitude(value: unknown): boolean {
  return (
    validateCoordinate(value, {
      min: -90,
      max: 90,
      regex: LATITUDE_REGEX,
      errorKey: 'validLatitude',
    }) === null
  );
}

export function isValidLongitude(value: unknown): boolean {
  return (
    validateCoordinate(value, {
      min: -180,
      max: 180,
      regex: LONGITUDE_REGEX,
      errorKey: 'validLongitude',
    }) === null
  );
}

export function isValidLngLatPair(value: unknown): value is [number, number] {
  if (!Array.isArray(value) || value.length !== 2) return false;
  const [lng, lat] = value as [unknown, unknown];
  return isValidLongitude(lng) && isValidLatitude(lat);
}
